import { useCallback, useEffect, useRef } from "react"

const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000 // 15 minutes

/**
 * Manages Campus Flow session lifecycle: start, end, inactivity timeout, and interaction tracking.
 *
 * @param {Object} mapAnalytics - Map analytics tracking methods from useAnalyticsContext().map
 * @returns {{ startSession: () => void, endSession: () => void, trackInteraction: (interactionType: string) => void }}
 */
export function useCampusFlowSession(mapAnalytics) {
  const sessionIdRef = useRef(null)
  const timeoutRef = useRef(null)

  // Keep a ref to mapAnalytics so callbacks have stable identities regardless
  // of analytics context re-renders (e.g. isOptedIn changes recreate capture → map).
  // Assigned synchronously so it is current on the same render.
  const mapAnalyticsRef = useRef(mapAnalytics)
  mapAnalyticsRef.current = mapAnalytics

  const clearInactivityTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const endSession = useCallback(() => {
    clearInactivityTimer()
    sessionIdRef.current = null
  }, [clearInactivityTimer])

  const resetInactivityTimer = useCallback(() => {
    clearInactivityTimer()
    // Note: directly clearing state here instead of calling endSession() to avoid
    // circular dependency (resetInactivityTimer <-> endSession <-> clearInactivityTimer).
    // If endSession gains side effects, update this path too.
    timeoutRef.current = setTimeout(() => {
      sessionIdRef.current = null
    }, INACTIVITY_TIMEOUT_MS)
  }, [clearInactivityTimer])

  const startSession = useCallback(() => {
    // Idempotent: no-op if session already active (prevents duplicate starts
    // from concurrent mount + state-change effects)
    if (sessionIdRef.current) return
    clearInactivityTimer()
    const newSessionId = crypto.randomUUID()
    sessionIdRef.current = newSessionId
    mapAnalyticsRef.current.trackCampusFlowSessionStart({ session_id: newSessionId })
    resetInactivityTimer()
  }, [clearInactivityTimer, resetInactivityTimer])

  const trackInteraction = useCallback(
    interactionType => {
      // Auto-start a new session if previous one timed out (campusFlow still on)
      if (!sessionIdRef.current) {
        startSession()
      }
      mapAnalyticsRef.current.trackCampusFlowInteraction({
        interaction_type: interactionType,
        session_id: sessionIdRef.current,
      })
      resetInactivityTimer()
    },
    [startSession, resetInactivityTimer]
  )

  // Clean up inactivity timer on unmount to prevent state updates after unmount
  useEffect(() => {
    return () => {
      clearInactivityTimer()
    }
  }, [clearInactivityTimer])

  return { startSession, endSession, trackInteraction }
}
